import ctaMan from "assets/images/psp/inlineCTA.jpg";
import illustrationSearch from "assets/images/psp/illustration-search.svg";
import GeorgeRansom from "assets/images/psp/review-george.png";
import AnnAlarcon from "assets/images/psp/review-ann.png";
import SMurphy from "assets/images/psp/review-murphy.png";
import BikeStorageImage from "assets/images/psp/bike-storage.webp";
import GarageVsStorageImage from "assets/images/psp/garage-vs-storage.webp";
import DriveUpUnitImage from "assets/images/psp/drive-up-unit.webp";
import HeaderBackgroundDesktop from "assets/images/psp/hero/bicycle-hero.webp";
import HeaderBackgroundMobile from "assets/images/psp/hero/bicycle-header-mobile.webp";
import HeaderBackgroundTablet from "assets/images/psp/hero/bicycle-header-tablet.webp";
import { storageCategories } from "pages/PSPPage/utils/getPSPLinks";

const faqs = [
	{
		question: "Can you store two bikes in a 5x5 storage unit?",
		answer: "Yes, you can typically store two bikes in a 5x5 storage unit, especially if you use vertical storage methods or bike hooks. A 5x5 unit provides 25 square feet of floor space, which is sufficient for two standard bicycles when stored efficiently.\n\nHanging bikes vertically on walls or using floor stands maximizes space utilization. However, consider the types of bikes you're storing – road bikes take less space than mountain bikes with wider handlebars, and electric bikes are heavier and may require ground storage.\n\nIf you also need space for helmets, gear, or maintenance supplies, a 5x10 unit might be more practical for long-term storage comfort."
	},
	{
		question: "Can I store my bike in a vertical storage unit?",
		answer: "Absolutely. Storing bicycles vertically is one of the most space-efficient methods in storage units. Most storage facilities allow vertical bike storage using wall-mounted hooks, bike stands, or hanging systems.\n\nVertical storage works well for all bike types including road bikes, mountain bikes, and hybrids. When storing bikes vertically, ensure the storage unit has adequate ceiling height (typically 8+ feet), use proper hooks or stands designed for bicycle weight, and position bikes so they don't interfere with unit access.\n\nSome cyclists prefer alternating wheel positions when storing multiple bikes vertically to maximize space efficiency and prevent handlebar interference."
	},
	{
		question:
			"What's the difference between bike storage units and regular storage units?",
		answer: "Bike storage units are regular storage units used specifically for bicycle storage, though some facilities offer specialized features that benefit cyclists. Standard storage units work perfectly well for bicycles when properly organized.\n\nHowever, some facilities provide bike-specific amenities like higher ceilings for vertical storage, ground-level access to avoid stairs, enhanced security due to bicycles' theft risk, and occasionally bike maintenance areas or air pumps.\n\nThe main difference is often in location convenience and access policies rather than the physical unit structure. Urban facilities may offer bike lockers or shared bike storage areas designed specifically for commuter cyclists."
	},
	{
		question: "How should I prepare my bicycle for storage?",
		answer: "Proper bicycle preparation prevents damage and maintains performance during storage periods. Start by thoroughly cleaning your bike to remove dirt, salt, and grime that can cause corrosion. Lubricate the chain, derailleurs, and other moving parts, but avoid over-lubrication, which attracts dirt.\n\nInflate tires to proper pressure to prevent flat spots and rim damage, though some cyclists prefer slightly lower pressure for very long-term storage. If storing for extended periods, consider removing the battery from electronic components like bike computers or lights. Store bikes in a dry area and consider using bike covers for additional protection. For valuable bikes, document serial numbers and take photos for insurance purposes."
	},
	{
		question: "What size storage unit do I need for multiple bicycles?",
		answer: "Storage unit size depends on the number of bikes, their types, and the additional gear you plan to store. A 5x5 unit typically accommodates 2-3 bikes with minimal gear, while a 5x10 unit comfortably stores 4-6 bikes plus helmets, tools, and accessories.\n\nFor cycling families or serious enthusiasts with multiple bike types, a 10x10 unit provides ample space for 8-10 bikes plus maintenance equipment, spare parts, and seasonal gear. Consider your storage method – vertical storage maximizes capacity, while ground storage requires more space but easier access.\n\nFactor in future bike purchases and seasonal gear storage when selecting unit size, as most facilities allow easy upgrades but size reductions may have waiting periods."
	}
];

const relatedContentCards = [
	{
		category: "Recreational Storage",
		url: "https://www.storage.com/blog/storing-bike-downtown-storage-unit/",
		date: "November 12, 2026",
		image: BikeStorageImage,
		content: "Downtown Bicycle Storage: Complete Guide to Safe Bike Storage"
	},
	{
		category: "Self Storage",
		url: "https://www.storage.com/blog/garage-vs-storage-unit-5-benefits-of-self-storage/",
		date: "December 1, 2025",
		image: GarageVsStorageImage,
		content: "Garage vs Storage Unit: 5 Benefits of Self Storage"
	},
	{
		category: "Self Storage",
		url: "https://www.storage.com/blog/drive-up-vs-first-floor-access-self-storage/",
		date: "June 2, 2025",
		image: DriveUpUnitImage,
		content:
			"First Floor Storage vs Drive-Up Storage: Your Complete Guide to Ground Level Storage Units"
	}
];

const pspLinksContent = {
	title: "More Ways to Store",
	image: illustrationSearch,
	storageTypes: storageCategories.bicycle
};

const reviewsContent = {
	title: "Storage.com has a 4.4 out of 5 star rating on",
	testimonials: [
		{
			body: "Great experience with my storage! I love the 'drive right in' climate-controlled storage area. Staff was super helpful with telling me about the system they use and setting me up.",
			author: "Jachooz",
			title: "Great experience with my storage",
			imageSrc: GeorgeRansom
		},
		{
			body: "Very knowledgeable and detail-oriented. The storage facility the Storage.com rep recommended is very close to where I live and at a reasonable rate. Climate-controlled and state-of-the-art security, which I can appreciate. Thanks again for all of your help and information.",
			author: "C. Meredith",
			title: "Excellent Customer Service",
			imageSrc: SMurphy
		},
		{
			body: "I had never used a storage unit before and had many questions regarding the necessary size, locations, climate-controlled, etc. Storage.com's rep made sure all of my questions were answered and the reservation was made.",
			author: "Hilary Martin",
			title: "Excellent help when I needed it!",
			imageSrc: AnnAlarcon
		}
	]
};

export const PSPBikeContent = {
	type: "Bicycle",
	sections: [
		{
			title: "Hero Section",
			noPadding: true,
			modules: [
				{
					type: "hero-section",
					props: {
						title: "Find the Top Bicycle Storage Units",
						description:
							"Your bike isn't just a transportation device - it's a fitness tool, a vehicle for weekend exploration, and a financial investment. Don't let apartment space limitations, garage overflow, or weather exposure put your bikes at risk. Professional bike storage units provide the secure, organized space your bicycles need to stay in peak condition.\n\nStorage.com connects you with verified bicycle storage facilities nationwide, so you can store with confidence and ride whenever adventure calls.",
						searchPlaceholder: "Search by city, state, or zip...",
						buttonText: "Find Bicycle Storage",
						heroImages: {
							desktop: HeaderBackgroundDesktop,
							tablet: HeaderBackgroundTablet,
							mobile: HeaderBackgroundMobile
						},
						heroAltText: "Bicycle Storage Hero Image"
					}
				}
			]
		},
		{
			title: "Why Bicycle Storage Matters",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "Why Bicycle Storage Matters More Than You Think",
						background: "white",
						content: [
							{
								id: "1",
								value: "Every cyclist faces the same storage dilemma: Your apartment lacks secure bike storage, your garage is packed with cars and equipment, and leaving bikes outside invites theft and weather damage. Quality bicycles cost hundreds to thousands of dollars, yet many owners resort to cramped hallways, outdoor racks, or basement corners that expose bikes to moisture, theft, and damage."
							},
							{
								id: "2",
								value: "Storage units for bikes eliminate these problems affordably. Professional bicycle storage facilities provide secure, climate-controlled environments that protect your investment while freeing up valuable living space. Instead of risking theft or dealing with cluttered homes, you get organized storage that keeps your bikes ready to ride."
							}
						],
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "Bicycle Storage Solutions",
			noPadding: true,
			modules: [
				{
					type: "size-comparison",
					props: {
						background: "light-blue",
						title: "Bicycle Storage Solutions by Cyclist Type",
						description:
							"Cyclists have options when it comes to bicycle storage. From indoor units to drive up access and every amenity in between, here are the top picks for how to store a bike.",
						sizes: [
							{
								id: "indoor-storage",
								label: "Indoor Storage",
								title: "Indoor Storage",
								description:
									"**Best For:**\n* Seasonal storage\n* Casual riders"
							},
							{
								id: "climate-control",
								label: "Climate Control",
								title: "Climate Control",
								description:
									"**Best For:**\n* Premium bike protection\n* Humid climates\n* Long term storage"
							},
							{
								id: "xl-storage",
								label: "XL Storage Unit",
								title: "XL Storage Unit",
								description:
									"**Best For:**\n* Bike collections\n* Bike business fleets\n* Gear storage"
							},
							{
								id: "drive-up",
								label: "Drive Up Access",
								title: "Drive Up Access",
								description:
									"**Best For:**\n* Frequent access\n* Workshop space\n* Collections"
							}
						]
					}
				}
			]
		},
		{
			title: "Indoor vs Outdoor Options",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "Indoor vs. Outdoor Bicycle Storage Unit Options",
						background: "blue",
						content: [
							{
								id: "1",
								value: "Many cyclists wonder about the best outdoor bicycle storage versus indoor alternatives. While outdoor bicycle storage can work for short-term needs, indoor bicycle storage unit options provide superior protection against theft, weather, and vandalism."
							},
							{
								id: "2",
								value: "**Indoor bicycle storage advantages:**\n* Complete protection from weather and UV damage\n* Enhanced security with gated access and surveillance\n* Climate control prevents rust and component degradation\n* Organized space for maintenance and gear storage\n* No seasonal limitations or access restrictions"
							},
							{
								id: "3",
								value: "**Outdoor storage considerations:**\n* Lower cost but higher theft and damage risk\n* Weather exposure can cause rust and component wear\n* Limited security compared to indoor facilities\n* May require additional weatherproofing measures"
							}
						],
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "Storage Ideas for Different Situations",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "Bicycle Storage Ideas for Different Living Situations",
						background: "light-blue",
						listType: "single",
						content: [
							{
								id: "1",
								value: "Different cyclists have varying storage needs based on their living arrangements and riding habits:"
							},
							{
								id: "2",
								value: "**Urban Apartment Dwellers:** Compact storage for 1-2 bikes with easy access for daily commuting"
							},
							{
								id: "3",
								value: "**Suburban Families:** Multi-bike storage with space for helmets, gear, and maintenance supplies"
							},
							{
								id: "4",
								value: "**Serious Cyclists:** Climate-controlled storage for high-value road bikes, mountain bikes, and racing equipment"
							},
							{
								id: "5",
								value: "**Seasonal Riders:** Cost-effective storage during winter months or off-season periods"
							},
							{
								id: "6",
								value: "**Collectors:** Specialized storage for vintage bikes, multiple disciplines, or investment-grade bicycles"
							}
						],
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "What Makes Professional Storage Different",
			noPadding: true,
			modules: [
				{
					type: "video-content",
					props: {
						background: "blue",
						description: "",
						title: "What Makes Professional Bicycle Storage Different",
						additionalContent: true,
						textAlign: "left",
						markDown:
							"Facilities specializing in bicycle storage understand cyclists' unique needs:\n\n* **Appropriate Access:** Wide doors and ground-level access designed for easy bike maneuvering\n* **Vertical Storage Options:** Higher ceilings to accommodate bike racks and vertical storage systems\n* **Security Features:** Enhanced security recognizing bicycles' high theft risk and portability\n* **Clean Environment:** Well-maintained facilities that protect bikes from dirt and moisture\n* **Flexible Access:** Extended hours accommodating early morning and evening riding schedules\n* **Additional Amenities:** Some locations offer bike maintenance areas, air pumps, and washing stations",
						mediaType: "video",
						mediaUrl: "qoofQz3ShSE",
						videoTitle:
							"What Makes Professional Bicycle Storage Different"
					}
				}
			]
		},
		{
			title: "Bicycle Storage CTA",
			noPadding: true,
			modules: [
				{
					type: "inline-cta",
					props: {
						storageType: "Bicycle",
						subheading: "Make a free reservation in minutes!",
						searchPlaceholder: "Search by city, state, or zip...",
						searchButtonText: "Find Storage",
						image: ctaMan,
						background: "white",
						imageAltText: "Find bicycle storage"
					}
				}
			]
		},
		{
			title: "Customer Reviews",
			noPadding: true,
			modules: [
				{
					type: "reviews",
					props: reviewsContent
				}
			]
		},
		{
			title: "Bicycle Storage FAQs",
			noPadding: true,
			modules: [
				{
					type: "faq",
					props: {
						title: "Bicycle Storage FAQs",
						faqs
					}
				}
			]
		},
		{
			title: "Bicycle Storage Related Content",
			noPadding: true,
			modules: [
				{
					type: "related-content",
					props: {
						background: "light-blue",
						heading: "Learn More About Bicycle Storage",
						unitCards: relatedContentCards
					}
				}
			]
		},
		{
			title: "Links to Other PSPs",
			noPadding: true,
			modules: [
				{
					type: "psp-links",
					props: {
						background: "white",
						storageGridContent: pspLinksContent
					}
				}
			]
		},
		{
			title: "Storage Near You",
			noPadding: true,
			modules: [
				{
					type: "nearby-cities",
					props: {
						background: "light-blue",
						storageType: "bike"
					}
				}
			]
		}
	],
	reviews: reviewsContent.testimonials.map((testimonial) => ({
		customerName: testimonial.author,
		reviewerName: testimonial.author,
		stars: "5",
		reviewRating: "5",
		text: testimonial.body,
		reviewText: testimonial.body
	}))
};

export default PSPBikeContent;
